import React, { useState, useCallback, useEffect, useRef } from "react";

import { getMonthlyStatistics } from "../../../services/dashboard";
import ReusableBarChart from "../../components/graphs/ReusableBarChart";
import { ProgressSpinner } from "primereact/progressspinner";
import { COCHARGE_DARK_GREEN } from "../../../styles/theme_constants";

const CURRENT_YEAR = new Date().getFullYear();

const initialStats = {
  year: CURRENT_YEAR,
  chargingSessions: {},
  chargingStationsCount: {},
  energyConsumption: {},
  totalRevenue: {},
};

export const AnnualBarCharts = ({ onLoaded }: { onLoaded?: () => void }) => {
  const [stats, setStats] = useState(initialStats);
  const [isLoading, setIsLoading] = useState(true);
  const [year, setYear] = useState(CURRENT_YEAR);

  const notified = useRef(false); // used for loader display

  const getYearlyStats = useCallback(
    async (selectedYear: number) => {
      setIsLoading(true);

      const result = await getMonthlyStatistics(selectedYear);
      const data =
        typeof result === "object" && !Array.isArray(result) && result?.energyConsumption
          ? { ...initialStats, ...result }
          : initialStats;
      setStats(data);
      setIsLoading(false);

      if (!notified.current) {
        notified.current = true;
        onLoaded?.();
      }
    },
    [onLoaded],
  );

  useEffect(() => {
    getYearlyStats(year);
  }, [year, getYearlyStats]);

  return (
    <div className="col-12">
      <div className="card">
        <div className="flex align-items-center justify-content-between mb-3">
          <div className="flex align-items-center gap-2">
            <i
              className="pi pi-chart-bar"
              style={{ color: COCHARGE_DARK_GREEN, fontSize: "1.25rem" }}
            />
            <span className="text-xl font-semibold text-900">
              Yearly Trends
            </span>
          </div>
          <div
            className="flex align-items-center gap-1 border-round-3xl"
            style={{
              background: "var(--surface-100)",
              padding: "2px 10px",
              border: "1px solid var(--surface-border)",
            }}
          >
            <button
              aria-label="Previous year"
              className="flex align-items-center justify-content-center cursor-pointer"
              style={{ width: 28, height: 28, color: COCHARGE_DARK_GREEN, background: "none", border: "none", padding: 0 }}
              onClick={() => setYear((y) => y - 1)}
            >
              <i className="pi pi-chevron-left text-sm" />
            </button>
            <span
              className="font-bold text-xl text-900 px-2"
              style={{ minWidth: "4rem", textAlign: "center" }}
            >
              {year}
            </span>
            <button
              aria-label="Next year"
              disabled={year >= CURRENT_YEAR}
              className="flex align-items-center justify-content-center"
              style={{
                width: 28,
                height: 28,
                color: year >= CURRENT_YEAR ? "var(--text-color-secondary)" : COCHARGE_DARK_GREEN,
                background: "none",
                border: "none",
                padding: 0,
                cursor: year >= CURRENT_YEAR ? "default" : "pointer",
              }}
              onClick={() => setYear((y) => y + 1)}
            >
              <i className="pi pi-chevron-right text-sm" />
            </button>
          </div>
        </div>

        <div
          style={{
            borderTop: "1px solid var(--surface-border)",
            marginBottom: "1.25rem",
          }}
        />

        {isLoading ? (
          <div className="flex justify-content-around py-5">
            {["Energy", "Sessions", "Charging Points"].map((label) => (
              <div
                key={label}
                className="flex flex-column align-items-center gap-2"
              >
                <ProgressSpinner style={{ width: 40, height: 40 }} />
                <span className="text-color-secondary text-sm">{label}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid m-0">
            <div
              className="col-12 md:col-4"
              style={{ borderRight: "1px solid var(--surface-border)" }}
            >
              <ReusableBarChart
                title="Energy Consumption"
                unit="kWh"
                datasetLabels={["Energy [kWh]"]}
                labels={Object?.keys(stats?.energyConsumption) || []}
                values={Object?.values(stats?.energyConsumption) || []}
              />
            </div>
            <div
              className="col-12 md:col-4"
              style={{ borderRight: "1px solid var(--surface-border)" }}
            >
              <ReusableBarChart
                title="Charging Sessions"
                unit="count"
                datasetLabels={["Sessions/Month"]}
                labels={Object?.keys(stats?.chargingSessions) || []}
                values={Object?.values(stats?.chargingSessions) || []}
              />
            </div>
            <div className="col-12 md:col-4">
              <ReusableBarChart
                title="Active Charging Points"
                unit="count"
                datasetLabels={["Charging Points"]}
                labels={Object?.keys(stats?.chargingStationsCount) || []}
                values={Object?.values(stats?.chargingStationsCount) || []}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
